import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { fg } from '@confluence/feature-gating';

import { ExperimentalAutomationRuleState as RuleState } from '../__types__/AutomationRuleSummaryQuery.ts';
import type { AutomationRuleSummary } from '../__types__/AutomationRuleSummaryQuery.ts';
import { ButtonState } from '../buttonStates.ts';

export type GetButtonStateProps = {
	automationRuleSummaryData?: AutomationRuleSummary;
	automationRuleSummaryLoading: boolean;
	isSmartButtonAllowed: boolean;
	contentType: string;
	isRunnableRule: boolean;
	isViewMode: boolean;
	isAdminHubAIEnabled: boolean;
	ruleId?: string;
	spaceId?: string;
	scopeId?: string;
	predefinedTemplateId?: string;
	isCurrentPageLive: boolean;
	error?: ApolloErrorInterop;
};

export const getButtonState = ({
	automationRuleSummaryData,
	automationRuleSummaryLoading,
	isSmartButtonAllowed,
	contentType,
	isRunnableRule,
	isViewMode,
	isAdminHubAIEnabled,
	ruleId,
	spaceId,
	scopeId,
	predefinedTemplateId,
	isCurrentPageLive,
	error,
}: GetButtonStateProps): ButtonState => {
	const automationRuleSummary =
		automationRuleSummaryData?.experimentalAutomationRuleSummary ?? undefined;

	const templateComponents = automationRuleSummary?.componentSummaries;
	const containsAIActions =
		templateComponents &&
		templateComponents.some(
			(component) =>
				component.type === 'confluence.generate-ai-action-items.action' ||
				component.type === 'confluence.generate-ai-summary.action',
		);

	if (automationRuleSummaryLoading) {
		return ButtonState.LOADING;
	}
	// button was created and edition was downgraded to non-premium
	else if (!isSmartButtonAllowed) {
		return ButtonState.NOT_AVAILABLE;
	} else if (contentType !== 'page') {
		return ButtonState.INVALID_CONTENT_TYPE;
	}
	// rule no longer exists either because it was moved or deleted
	else if (!automationRuleSummary && ruleId) {
		// rule moved from another space
		if (spaceId !== scopeId) {
			return ButtonState.RULE_MOVED;
		}
		// rule is deleted from automation rule list
		else {
			return ButtonState.RULE_DELETED;
		}
	}
	// rule state is disabled
	else if (automationRuleSummary?.ruleState === RuleState.DISABLED) {
		return ButtonState.RULE_DISABLED;
	}
	// AI has been disabled and the rule contains AI actions
	else if (!isAdminHubAIEnabled && containsAIActions) {
		return ButtonState.AI_DISABLED;
	}
	// Handle edit mode specific states
	else if (!isViewMode) {
		// user is missing permissions to view rule config
		if (error) {
			return ButtonState.MISSING_PERMISSIONS;
		}
		if (!ruleId) {
			if (!!predefinedTemplateId) {
				return ButtonState.PREDEFINED_TEMPLATE_NOT_CONFIGURED;
			} else {
				return ButtonState.EDIT_RULE_NOT_CONFIGURED;
			}
		}
		// Allow button to be runnable in live page edit mode if it has all required info
		if (ruleId && isRunnableRule && isCurrentPageLive && fg('a4c-enable-live-doc-smart-button')) {
			return ButtonState.RULE_CONFIGURED;
		}
		return ButtonState.EDIT_RULE_CONFIGURED;
	}
	// Handle view mode specific states
	// missing info to run rules in view mode
	else if (ruleId && !isRunnableRule) {
		return ButtonState.RULE_NOT_RUNNABLE;
	}
	// button not configured
	else if (!ruleId) {
		return ButtonState.VIEW_RULE_NOT_CONFIGURED;
	} else {
		return ButtonState.RULE_CONFIGURED;
	}
};
